// Imperative helpers for embedding the Walley payment iframe.
//
// Walley ships a "loader script" that, when added to the DOM, finds a target
// div by id (via its `data-container-id` attribute) and renders an iframe
// inside it. The script must come AFTER its target div in document order, or
// the loader bails silently — see Walley docs §2 and the gotchas list at the
// bottom of the integration doc.
//
// The iframe Walley renders has a hard ~397 px minimum content width. Phones
// in portrait (typically 360 px and below) clip the right edge unless we
// scale the inner container down with a CSS transform. Walley resizes its
// own iframe between flow steps (method picker → form), so we re-apply the
// scale on every iframe resize.
//
// All DOM mutations are reversed by the handle returned from
// `mountWalleyIframe` — call `destroy()` on unmount or before re-mounting.

const WALLEY_MIN_WIDTH = 397;
const LOADER_SCRIPT_ID_PREFIX = 'walley-loader-script-';
const ALLOWED_SCRIPT_DOMAINS = ['walleydev.com', 'walleypay.com'];

function validateLoaderScriptUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return null;
    const isAllowed = ALLOWED_SCRIPT_DOMAINS.some(
      d => parsed.hostname === d || parsed.hostname.endsWith('.' + d),
    );
    return isAllowed ? url : null;
  } catch {
    return null;
  }
}

export interface MountWalleyIframeArgs {
  loaderScript: string;
  publicToken: string;
  containerId: string;
  outer: HTMLElement;
  inner: HTMLElement;
  onError?: (err: Event | string) => void;
  /** Fires once the Walley iframe element appears in the inner container.
   *  Useful for hiding a placeholder/loading state. Idempotent — only fires
   *  for the first iframe detected. */
  onIframeReady?: (iframe: HTMLIFrameElement) => void;
  /** Two-letter language code forwarded to Walley as `data-lang` on the
   *  loader script. Walley's resolution order is data-lang > <html lang> >
   *  market default; without this attribute Walley reads the host page's
   *  <html lang>, which on WP is set by the theme — not the RRM language
   *  toggle. Pass the customer-selected language (e.g. 'fi' or 'en') so the
   *  iframe matches the rest of the booking flow. */
  lang?: string;
}

export interface WalleyIframeHandle {
  destroy(): void;
}

// Computes the scale factor needed to fit Walley's fixed-width iframe inside
// a narrower outer container, and applies it to the inner div. Resets and
// no-ops when the available width is already ≥ WALLEY_MIN_WIDTH.
export function applyWalleyScale(outer: HTMLElement, inner: HTMLElement): void {
  // Reset before measuring so the natural dimensions are reliable.
  inner.style.transform = '';
  inner.style.width = '';
  outer.style.height = '';

  const available = outer.offsetWidth;
  if (!available || available >= WALLEY_MIN_WIDTH) return;

  const scale = available / WALLEY_MIN_WIDTH;
  inner.style.width = WALLEY_MIN_WIDTH + 'px';
  inner.style.transformOrigin = 'top left';
  inner.style.transform = `scale(${scale})`;
  // Prefer the iframe's own height once Walley has injected it. `inner` has a
  // 400px min-height (so the placeholder area doesn't collapse before the
  // iframe loads) — once the real iframe is present, that floor inflates
  // `inner.offsetHeight` past the actual content height for shorter Walley
  // flow steps and leaves a visible gap below the iframe on narrow viewports.
  // Fall back to `inner.offsetHeight` only while the iframe isn't there yet.
  const iframe = inner.querySelector('iframe');
  const measuredHeight = iframe ? iframe.offsetHeight : inner.offsetHeight;
  // After the transform is applied, the measured height is still the
  // un-scaled value, so we multiply by the scale factor to get the visual
  // height the outer container needs to reserve.
  outer.style.height = Math.ceil(measuredHeight * scale) + 'px';
}

// Mounts the Walley loader script, wires observers for responsive scaling,
// and returns a destroy() handle that fully reverses every DOM and listener
// side-effect. Idempotent across re-mounts: any prior loader for the same
// containerId is removed before the new one is injected.
export function mountWalleyIframe(args: MountWalleyIframeArgs): WalleyIframeHandle {
  const { loaderScript, publicToken, containerId, outer, inner, onError, onIframeReady, lang } = args;

  // Defensive layout styles — match what the Walley docs prescribe so the
  // helper works even if the consumer has not yet added matching SCSS.
  outer.style.width = outer.style.width || '100%';
  outer.style.overflow = outer.style.overflow || 'hidden';
  if (!inner.style.minHeight) inner.style.minHeight = '400px';
  inner.style.transformOrigin = 'top left';

  // Loader script reads `data-container-id` then calls getElementById; ensure
  // the inner element actually carries that id even if the JSX forgot.
  if (inner.id !== containerId) {
    inner.id = containerId;
  }

  // Remove any prior loader for this container — re-mount, hot reload, retry.
  const loaderScriptId = LOADER_SCRIPT_ID_PREFIX + containerId;
  document.getElementById(loaderScriptId)?.remove();

  // Inject the loader. Must come after the target div in DOM order.
  // Validate the loader script URL against the Walley domain allowlist
  // to prevent script injection if the API response were compromised.
  const validatedSrc = validateLoaderScriptUrl(loaderScript);
  if (!validatedSrc) {
    const err = 'Walley loader script URL is not from an allowed domain: ' + loaderScript;
    onError?.(err);
    return { destroy() {} };
  }
  const script = document.createElement('script');
  script.id = loaderScriptId;
  script.src = validatedSrc;
  script.async = true;
  script.setAttribute('data-token', publicToken);
  script.setAttribute('data-container-id', containerId);
  if (lang) {
    script.setAttribute('data-lang', lang);
  }
  if (onError) {
    script.addEventListener('error', onError);
  }
  inner.after(script);

  // Watch for the iframe Walley injects, then keep its rendered size in sync
  // with the outer container as Walley re-renders between flow steps.
  let observedIframe: HTMLIFrameElement | null = null;
  const resizeObserver = new ResizeObserver(() => applyWalleyScale(outer, inner));
  let iframeReadyFired = false;
  const mutationObserver = new MutationObserver(() => {
    const iframe = inner.querySelector('iframe');
    if (!iframe || iframe === observedIframe) return;
    observedIframe = iframe;
    applyWalleyScale(outer, inner);
    resizeObserver.observe(iframe);
    if (!iframeReadyFired) {
      iframeReadyFired = true;
      try { onIframeReady?.(iframe); } catch { /* swallow consumer-callback errors */ }
    }
  });
  mutationObserver.observe(inner, { childList: true, subtree: true });

  const onWindowResize = () => applyWalleyScale(outer, inner);
  window.addEventListener('resize', onWindowResize);

  return {
    destroy() {
      window.removeEventListener('resize', onWindowResize);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      observedIframe = null;

      const stale = document.getElementById(loaderScriptId);
      if (stale) {
        if (onError) stale.removeEventListener('error', onError);
        stale.remove();
      }

      // Best-effort: clear the iframe Walley left behind so the next mount
      // starts from a clean container.
      while (inner.firstChild) inner.removeChild(inner.firstChild);

      // Reset inline styles we added so the next mount re-measures fresh.
      inner.style.transform = '';
      inner.style.width = '';
      outer.style.height = '';
    },
  };
}

// Exposed for tests.
export const __WALLEY_MIN_WIDTH = WALLEY_MIN_WIDTH;
